<template>
  <!-- App bar: indigo-lighten-2 background, flat with subtle bottom border -->
  <v-app-bar :elevation="0" color="grey-darken-4" density="comfortable" border="b thin opacity-25">
    <!-- Hamburger / close toggle (authenticated pages only) -->
    <v-btn
      icon
      variant="text"
      @click.stop="drawer = !drawer"
      v-if="isAuthenticated && $route.name !== 'NoAccess' && $route.name !== 'AccessDenied'"
    >
      <v-icon>{{ drawer ? "mdi-close" : "mdi-menu" }}</v-icon>
    </v-btn>

    <!-- Spacer for unauthenticated pages -->
    <div v-if="!isAuthenticated" class="ml-4" />

    <!-- Standard app title -->
    <v-app-bar-title v-if="$route.name !== 'NoAccess' && $route.name !== 'AccessDenied'">
      <v-icon
        v-if="currentAppConfig?.icon"
        size="small"
        :icon="currentAppConfig.icon"
        :color="currentAppConfig.iconColor"
        style="margin-right: 2px"
      /><template v-if="!(smAndDown && isWarehouseRoute)"
        ><span :class="currentAppConfig?.iconColor ? `text-${currentAppConfig.iconColor}` : ''">{{
          title
        }}</span
        ><!-- The open section, in the colour that section is known by; the
             module's own name keeps the sidebar's colour.
        --><span v-if="pageSection" :class="pageSection.color ? `text-${pageSection.color}` : ''">
          &nbsp;— {{ pageSection.title }}</span
        ></template
      >
      <!-- Audit log total count chip — injected by AuditLogs.vue -->
      <v-chip
        v-if="route.name === 'AuditLogs' && auditTotalLogs > 0"
        size="x-small"
        variant="tonal"
        color="grey-darken-1"
        class="ml-2"
      >
        {{ auditTotalLogs.toLocaleString() }}
      </v-chip>
      <!-- Contextual info chip — injected by the current page via useAppBarActions
           (record count on Sales Analytics, current client on Fusion Eat) -->
      <v-chip
        v-if="infoChip && (isSalesRoute || routeAppIs('meals'))"
        size="x-small"
        :color="infoChip.chipColor === null ? undefined : infoChip.chipColor || 'primary'"
        :variant="infoChip.chipVariant || 'tonal'"
        class="ml-2"
      >
        <v-icon start size="x-small">{{ infoChip.icon || "mdi-database" }}</v-icon>
        {{ infoChip.chip }}
      </v-chip>
    </v-app-bar-title>

    <template v-slot:append>
      <template v-if="$route.name !== 'NoAccess' && $route.name !== 'AccessDenied'">
        <!-- System Settings back button — shown on settings sub-pages for quick navigation back -->
        <div class="d-inline-block" v-if="isSettingsRoute && route.name !== 'Settings'">
          <v-btn icon variant="text" @click="router.push('/settings')">
            <v-icon color="grey-lighten-1">mdi-cog</v-icon>
          </v-btn>
          <v-tooltip activator="parent" location="bottom">System Settings</v-tooltip>
        </div>

        <!-- Warehouse month picker — centered, shown on all warehouse pages -->
        <div
          v-if="routeAppIs('warehouse') && isWarehouseRoute"
          style="position: absolute; left: 50%; transform: translateX(-50%); z-index: 1"
        >
          <WarehouseMonthPicker />
        </div>

        <!-- Warehouse home button — shown on warehouse sub-pages for quick navigation back -->
        <div
          class="d-inline-block"
          v-if="routeAppIs('warehouse') && isWarehouseRoute && route.name !== 'WarehouseInventory'"
        >
          <v-btn icon variant="text" @click="router.push('/accounting/warehouse')">
            <v-icon :color="MODULES.warehouse.color">{{ MODULES.warehouse.icon }}</v-icon>
          </v-btn>
          <v-tooltip activator="parent" location="bottom">Warehouse Home</v-tooltip>
        </div>

        <!-- Reviews home button — shown on reviews sub-pages for quick navigation back -->
        <div
          class="d-inline-block"
          v-if="routeAppIs('reviews') && isReviewsRoute && route.name !== 'ReviewsLanding'"
        >
          <v-btn icon variant="text" @click="router.push('/reviews')">
            <!-- Reviews is a Marketing page: module colour, own icon (moduleVisuals.js) -->
            <v-icon :color="MODULES.marketing.color">mdi-star</v-icon>
          </v-btn>
          <v-tooltip activator="parent" location="bottom">Reviews Home</v-tooltip>
        </div>

        <!-- HR home button — shown on HR sub-pages for quick navigation back -->
        <div class="d-inline-block" v-if="route.name === 'HrEmployees' || route.name === 'HrPayroll'">
          <v-btn icon variant="text" @click="router.push('/hr')">
            <v-icon :color="MODULES.hr.color">{{ MODULES.hr.icon }}</v-icon>
          </v-btn>
          <v-tooltip activator="parent" location="bottom">HR Home</v-tooltip>
        </div>

        <!-- Marketing home button — shown on disputes page for quick navigation back -->
        <div class="d-inline-block" v-if="route.name === 'DisputesHome'">
          <v-btn icon variant="text" @click="router.push('/marketing')">
            <v-icon :color="MODULES.marketing.color">{{ MODULES.marketing.icon }}</v-icon>
          </v-btn>
          <v-tooltip activator="parent" location="bottom">Marketing Home</v-tooltip>
        </div>

        <!-- Page period picker — a page registers { month, onMonth } via
             useAppBarActions (Payroll); centered like the warehouse picker -->
        <div
          v-if="monthAction"
          style="position: absolute; left: 50%; transform: translateX(-50%); z-index: 1; max-width: 220px"
        >
          <v-text-field
            :model-value="monthAction.month"
            type="month"
            density="compact"
            variant="outlined"
            hide-details
            base-color="white"
            theme="dark"
            style="min-width: 140px"
            @update:model-value="(v) => v && monthAction.onMonth(v)"
          />
          <v-tooltip activator="parent" location="bottom">{{ monthAction.tooltip || "Period" }}</v-tooltip>
        </div>

        <!-- Dynamic action buttons injected by pages via useAppBarActions -->
        <template v-for="action in visibleActions" :key="action.tooltip">
          <div class="d-inline-block">
            <v-btn
              icon
              :variant="action.variant || 'text'"
              @click="action.onClick"
              :loading="action.loading"
              :color="action.color"
            >
              <v-badge
                v-if="action.badge"
                :content="action.badge.content"
                :model-value="action.badge.show !== false"
                :color="action.badge.color || 'error'"
                :dot="action.badge.dot"
                floating
                class="app-bar-badge"
              >
                <v-icon>{{ action.icon }}</v-icon>
              </v-badge>
              <v-icon v-else>{{ action.icon }}</v-icon>
            </v-btn>
            <v-tooltip activator="parent" location="bottom">{{ action.tooltip }}</v-tooltip>
          </div>
        </template>

        <!-- Marketing (reviews) three-dot menu — shown on reviews pages within the marketing app -->
        <v-btn
          icon
          variant="text"
          @click="showReviewsMenu = true"
          v-if="routeAppIs('reviews') && isReviewsRoute && isAdmin"
        >
          <v-icon>mdi-dots-vertical</v-icon>
        </v-btn>

        <!-- Disputes three-dot menu — shown on disputes page -->
        <v-btn
          icon
          variant="text"
          @click="showDisputesMenu = true"
          v-if="route.name === 'DisputesHome'"
        >
          <v-icon>mdi-dots-vertical</v-icon>
        </v-btn>

        <!-- Contacts three-dot menu (admin only) -->
        <v-btn
          icon
          variant="text"
          @click="showContactsMenu = true"
          v-if="routeAppIs('contacts') && isAdmin"
        >
          <v-icon>mdi-dots-vertical</v-icon>
        </v-btn>

        <!-- Menu dots menu (renamed from Food Cost) -->
        <v-btn icon variant="text" @click="showMenuMenu = true" v-if="routeAppIs('menu')">
          <v-icon>mdi-dots-vertical</v-icon>
        </v-btn>

        <!-- Warehouse dots menu -->
        <v-btn
          icon
          variant="text"
          @click="showWarehouseMenu = true"
          v-if="(routeAppIs('warehouse') && isWarehouseRoute && isAdmin) || (isAdmin && (route.name === 'Delivery' || route.name === 'Items'))"
        >
          <v-icon>mdi-dots-vertical</v-icon>
        </v-btn>

        <!-- Journal dots menu -->
        <v-btn
          v-if="isJournalRoute && isAdmin"
          icon
          variant="text"
          @click="showJournalMenu = true"
        >
          <v-icon>mdi-dots-vertical</v-icon>
        </v-btn>

        <!-- Tasks dots menu -->
        <v-btn
          v-if="routeAppIs('tasks')"
          icon
          variant="text"
          @click="showTasksMenu = true"
        >
          <v-icon>mdi-dots-vertical</v-icon>
        </v-btn>
      </template>
    </template>
  </v-app-bar>
</template>

<script setup>
import { computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import { useApp } from "@/composables/useApp.js";
import { useAppBarActions } from "@/composables/useAppBarActions.js";
import { MODULES, moduleColor } from "@/config/moduleVisuals.js";
import { useAuditTotalLogs } from "@/composables/useAuditTotalLogs.js";
import WarehouseMonthPicker from "@/components/WarehouseMonthPicker.vue";

// Singleton — written by AuditLogs.vue, read here for the title chip
const { totalLogs: auditTotalLogsRef } = useAuditTotalLogs();
const auditTotalLogs = computed(() => auditTotalLogsRef.value);

const route = useRoute();
const router = useRouter();
const { smAndDown } = useDisplay();
const {
  isAuthenticated,
  isAdmin,
  showMainMenu,
  showReviewsMenu,
  showMenuMenu,
  showContactsMenu,
  showWarehouseMenu,
  showDisputesMenu,
  showJournalMenu,
  showTasksMenu,
  drawer,
} = useApp();
const { actions, clearActions, pageTitle, pageSection } = useAppBarActions();

// Centralized stale-action guard: clear page-injected app-bar actions on every
// route change. clearActions defers to nextTick and is skipped if the incoming
// page already called setActions in the same tick, so a page's own buttons are
// preserved — but actions leaking from a previous page (e.g. a page that didn't
// clean up on unmount, or pages like Home that never inject any) are removed.
watch(() => route.fullPath, () => clearActions());

// Helper: check if current route's meta.app matches a given app name (handles string or array)
const routeAppIs = (app) => {
  const metaApp = route.meta?.app;
  if (Array.isArray(metaApp)) return metaApp.includes(app);
  return metaApp === app;
};

// Reviews pages within the marketing app (for showing the reviews dot menu)
const reviewsRouteNames = [
  "ReviewsLanding",
  "ReviewsHome",
  "ReviewsToday",
  "ReviewsCalendar",
  "ReviewsClassic",
  "ReviewsImport",
  "ReviewsAdmin",
  "ReviewAssignments",
];
const isReviewsRoute = computed(() => reviewsRouteNames.includes(route.name));

// Settings sub-pages (all routes under /settings/* except the main Settings page itself)
const settingsRouteNames = [
  "UsersManagement",
  "AuditLogs",
  "OutletsSettings",
  "OptionsManagement",
  // "PlatformCredentials", // disabled — file import removed
  "AssetSettings",
  "TagsManagement",
  "AccountManagersManagement",
  "AddressBookSettings",
  "ReviewsAdmin",
  // "Documentation" intentionally excluded — it's a general page for all users,
  // not an appadmin-only settings sub-page, so it shouldn't show the
  // "back to System Settings" button (Settings is appadmin-only).
  "TasksSettings",
];
const isSettingsRoute = computed(() => settingsRouteNames.includes(route.name));

// Sales and warehouse sub-route detection within accounting app
const salesRouteNames = ["SalesAnalytics", "VisieraOrders"];
const warehouseRouteNames = [
  "WarehouseInventory",
  "WarehouseJournal",
  "WarehouseLedger",
  "WarehouseParStock",
  "WarehouseMonthlySummary",
];
const isSalesRoute = computed(() => salesRouteNames.includes(route.name));
const isWarehouseRoute = computed(() => warehouseRouteNames.includes(route.name));
// Pages that show the GL dots menu — each must render <GlMenuSheet>, or the
// button opens nothing. Not Settlements (Cheques & bank): its menu was only
// links the sidebar already has, and Back to Month End covers the way back.
// Not MonthEnd: it never rendered the sheet, so its button did nothing
// (Maurizio, 2026-09-28).
const journalRouteNames = ["Journal", "Ledger", "Coa", "TrialBalance", "Financials", "Periods", "FixedAssets", "CostsGL"];
const isJournalRoute = computed(() => journalRouteNames.includes(route.name));

// App-level config (used when no specific route config matches)
// Icons and colours come from config/moduleVisuals.js (one entry per module,
// shared with the sidebar and the Account page — Maurizio, 2026-09-10).
const vis = (key) => ({ icon: MODULES[key].icon, iconColor: MODULES[key].color });
const appConfig = {
  accounting: { title: "Accounting", ...vis("accounting") },
  marketing: { title: "Marketing", ...vis("marketing") },
  assets: { title: "Assets", ...vis("assets") },
  contacts: { title: "Contacts", ...vis("contacts") },
  menu: { title: "Menu", ...vis("menu") },
  warehouse: { title: "Warehouse", ...vis("warehouse") },
  hr: { title: "HR and Contacts", ...vis("hr") },
  tasks: { title: "Tasks", ...vis("tasks") },
  meals: { title: "Fusion Eat - Meals", ...vis("meals") },
};

// Per-route title overrides. A page inside a module keeps its own icon but
// takes the module colour (moduleColor) — see config/moduleVisuals.js.
const settingsEntry = { title: "System Settings", icon: "mdi-cog" };
const accountEntry = { title: "Account", icon: "mdi-account-cog" };
const salesEntry = vis("sales");
const routeConfig = {
  Home: { title: "Home", icon: "mdi-home" },
  MarketingHome: { title: "Marketing", ...vis("marketing") },
  Settings: settingsEntry,
  UsersManagement: { title: "User Management", icon: "mdi-account-group" },
  AuditLogs: { title: "Audit Logs", icon: "mdi-history" },
  OutletsSettings: { title: "Outlets", icon: "mdi-storefront-outline" },
  OptionsManagement: { title: "Options", icon: "mdi-cog" },
  // PlatformCredentials: { title: "Platform Credentials", icon: "mdi-cog" }, // disabled — file import removed
  TagsManagement: { title: "Tags", icon: "mdi-cog" },
  TasksSettings: { title: "Tasks Settings", ...vis("tasks") },
  ReviewsLanding: { title: "Reviews", icon: "mdi-star", iconColor: moduleColor("marketing") },
  ReviewsToday: { title: "Today's Assignments", icon: "mdi-star", iconColor: moduleColor("marketing") },
  ReviewsHome: { title: "Review Assignments", icon: "mdi-star", iconColor: moduleColor("marketing") },
  AddressBookSettings: { title: "Address Book Settings", icon: "mdi-cog-outline" },
  ReviewAssignments: { title: "Assignments", icon: "mdi-clipboard-check-outline", iconColor: moduleColor("marketing") },
  ReviewsCalendar: { title: "Assignments Calendar", icon: "mdi-calendar", iconColor: moduleColor("marketing") },
  ReviewsAdmin: { title: "Reviews", icon: "mdi-database-edit-outline", iconColor: moduleColor("marketing") },
  AccountManagersManagement: { title: "Account Managers", icon: "mdi-cog" },
  ApiInterface: { title: "API Interface", icon: "mdi-api" },
  Documentation: { title: "Documentation", icon: "mdi-book-open-variant" },
  Account: accountEntry,
  // The Menu module's landing page is the menu-items list; named so in the
  // sidebar ("Menu Items" child + subtitle) and here (Maurizio, 2026-09-10).
  MenuHome: { title: "Menu Items", ...vis("menu") },
  MenuIngredients: { title: "Ingredients", icon: "mdi-food-apple", iconColor: moduleColor("menu") },
  MenuRecipes: { title: "Recipes", icon: "mdi-file-document-outline", iconColor: moduleColor("menu") },
  MenuPackaging: { title: "Packaging", icon: "mdi-package-variant-closed", iconColor: moduleColor("menu") },
  MenuCategories: { title: "Categories", icon: "mdi-tag-multiple", iconColor: moduleColor("menu") },
  MenuCombos: { title: "Combos", icon: "mdi-food-variant", iconColor: moduleColor("menu") },
  Delivery: { title: "Delivery", icon: "mdi-truck-delivery", iconColor: moduleColor("warehouse") },
  Items: { title: "Items", icon: "mdi-format-list-bulleted", iconColor: moduleColor("warehouse") },
  WarehouseInventory: { title: "Inventory", ...vis("warehouse") },
  WarehouseJournal: { title: "Stock Journal", icon: "mdi-book-open-page-variant", iconColor: moduleColor("warehouse") },
  WarehouseLedger: { title: "Stock Ledger", icon: "mdi-book-open-variant", iconColor: moduleColor("warehouse") },
  WarehouseParStock: { title: "Par Stock", icon: "mdi-chart-bar", iconColor: moduleColor("warehouse") },
  WarehouseMonthlySummary: { title: "Monthly Summary", icon: "mdi-file-chart-outline", iconColor: moduleColor("warehouse") },
  ContactsCredentials: { title: "Credentials", icon: "mdi-shield-key", iconColor: moduleColor("contacts") },
  HrHome: { title: "HR and Contacts", ...vis("hr") },
  HrEmployees: { title: "HR Employees", icon: "mdi-account-group", iconColor: moduleColor("hr") },
  HrPayroll: { title: "Payroll", ...vis("payroll") },
  CampaignsHome: { title: "Campaigns", icon: "mdi-bullhorn", iconColor: moduleColor("marketing") },
  CampaignsCalendar: { title: "Campaign Calendar", icon: "mdi-calendar", iconColor: moduleColor("marketing") },
  CampaignsWeeklyCalendar: { title: "Weekly Calendar", icon: "mdi-calendar", iconColor: moduleColor("marketing") },
  DisputesHome: { title: "Disputes", icon: "mdi-file-document-outline", iconColor: moduleColor("marketing") },
  TasksHome: { title: "Tasks", ...vis("tasks") },
  SalesAnalytics: { title: "Sales Analytics", icon: "mdi-chart-line", iconColor: moduleColor("sales") },
  VisieraOrders: { title: "Manage Sales Data", icon: "mdi-database-edit-outline", iconColor: moduleColor("sales") },
  Journal: { title: "Journal", ...vis("accounting") },
  Ledger: { title: "Ledger", icon: "mdi-book-open-variant", iconColor: moduleColor("accounting") },
  Coa: { title: "Chart of Accounts", icon: "mdi-file-tree", iconColor: moduleColor("accounting") },
  TrialBalance: { title: "Trial Balance", icon: "mdi-scale-balance", iconColor: moduleColor("accounting") },
  Financials: { title: "Financials", icon: "mdi-file-document-outline", iconColor: moduleColor("accounting") },
  Periods: { title: "Periods", icon: "mdi-calendar-lock", iconColor: moduleColor("accounting") },
  FixedAssets: { title: "Fixed Assets", icon: "mdi-domain", iconColor: moduleColor("accounting") },
  // Route name is historical; to a user this page is the platform-cost Coverage
  // check ("Cost → GL" meant nothing to anyone — Maurizio, 2026-09-08).
  CostsGL: { title: "Platform Cost Coverage", icon: "mdi-table-check", iconColor: moduleColor("accounting") },
  PlatformCostCoverageSettings: { title: "Platform Cost Coverage Settings", icon: "mdi-table-cog", iconColor: moduleColor("accounting") },
  DirectUpload: { title: "Direct Upload", ...vis("directUpload") },
  CashDaybook: { title: "Cash Daybook", icon: "mdi-cash-register", iconColor: moduleColor("accounting") },
  MonthEnd: { title: "Month End", icon: "mdi-calendar-check", iconColor: moduleColor("accounting") },
  Settlements: { title: "Cheques & bank", icon: "mdi-swap-horizontal", iconColor: moduleColor("accounting") },
  ChequesInCirculation: { title: "Cheques & bank", icon: "mdi-checkbook", iconColor: moduleColor("accounting") },
};

// Resolve current page config
const currentAppConfig = computed(() => routeConfig[route.name] || appConfig[route.meta?.app]);
// A page that changes section without navigating sets its own title; the
// route's own is the fallback.
const title = computed(() => pageTitle.value || currentAppConfig.value?.title || "Pizza Felice LLC");

// Filtered actions (respect show flag).
// Plus ("add") actions are excluded here — they are rendered as a contextual
// floating action button (FAB) in App.vue instead of an app-bar icon button.
const visibleActions = computed(() =>
  actions.value.filter((a) => a.show !== false && !a.chip && !a.onMonth && a.icon !== "mdi-plus")
);
// Month picker registered by the page ({ month: "YYYY-MM", onMonth(v) }).
const monthAction = computed(() => actions.value.find((a) => a.onMonth && a.show !== false));
// Chip-style action shown inline in the title (e.g. sales analytics record
// count, or the Fusion Eat module's current-client indicator)
const infoChip = computed(() => actions.value.find((a) => a.chip));
</script>

<style scoped>
/* Remove default left margin on toolbar title */
:deep(.v-toolbar-title) {
  margin-inline-start: 0 !important;
}
/* Tighter gap between action buttons */
:deep(.v-toolbar__append) {
  gap: 4px;
}
/* Compact floating badge */
.app-bar-badge :deep(.v-badge__badge) {
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  font-size: 10px;
  top: -2px !important;
}
</style>
